import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Col, Form, message, Row, Select, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { LoadCanvasTemplate, loadCaptchaEnginge, validateCaptcha } from 'react-simple-captcha';
import { api, endpoints } from '../../api';
import { routerPath } from '../../router/routerPath';
import {
  BlueLayout,
  CustomButton,
  CustomMaskInput,
  CustomSelect,
  DownloadCol,
  ErrorMessage,
  FormBlock,
  FormInput,
  FormItem,
} from '../../styles/styled';
import { emailRegExp, lastNameRegExp, patronymicRegExp, userNameRegExp } from '../../utils/patterns';
import {
  FileEditorData,
  UploadComponent,
} from '../NewApplicationPage/components/BookRegistrationComponent/components/BookForm/UploadComponent/UploadComponent';
import { FileDataType, FileRequestType, PublishersType } from '../NewApplicationPage/types';
import { RegisterRepresentativeContainerUI } from './styled';
import { RegisterRepresentativeInputType, RegisterRepresentativeRequestType } from './types';

const { Option } = Select;

export const RegisterRepresentativePage = () => {
  const [inputsState, setInputs] = useState<RegisterRepresentativeInputType>({});
  const [captchaInputState, setCaptchaInput] = useState<string>('');
  const [captchaState, setCaptcha] = useState<boolean | null>(null);
  const [loadedFile, setLoadedFile] = useState<FileRequestType>();
  const [publishersList, setPublishersList] = useState<PublishersType>([]);
  const navigate = useNavigate();
  const postDataState = useRef(false);
  const [onFinishFailed, setOnFinishFailed] = useState<boolean>(false);

  useEffect(() => {
    loadCaptchaEnginge(5, 'white', 'black', 'lower');
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get<PublishersType>(endpoints.getPublicPublishers(0, 10000));

        setPublishersList(response.data);
      } catch (err) {}
    })();
  }, []);

  const uploadFiles = useCallback(async (fileData: FileDataType) => {
    try {
      const response = await api
        .post<{ success: boolean; file_id: number }>(endpoints.addPublicFile(), {
          ...fileData,
          ext: fileData.title.split('.').pop(),
        })
        .then((el) => {
          return el;
        });

      if (response) {
        setLoadedFile({ id: response.data.file_id, title: fileData.title });
      }
    } catch (err) {
      message.error('Произошла ошибка загрузки файла');
    }
  }, []);

  const beforeUploadFile = async (data: FileEditorData) => {
    await uploadFiles({
      base64: data.data.split('base64,')[1],
      title: data.name as string,
      ext: data.type as string,
    });
  };

  const onFileRemoveHandler = () => {
    setLoadedFile(undefined);
  };

  const requestForm: RegisterRepresentativeRequestType = useMemo(() => {
    const result = { ...inputsState };

    delete result.passwordRepeat;

    return {
      ...result,
      password: inputsState.password === inputsState.passwordRepeat ? result.password : null,
    };
  }, [inputsState]);

  const captchaSubmit = (): boolean => {
    setCaptcha(validateCaptcha(captchaInputState));

    return validateCaptcha(captchaInputState);
  };

  const submitForm = () => {
    setOnFinishFailed(false);

    if (captchaSubmit()) {
      postDataState.current = true;

      api
        .post(
          endpoints.registerRepresentative(),
          loadedFile?.id ? { ...requestForm, file: { id: loadedFile.id } } : requestForm,
        )
        .then(() => {
          localStorage.setItem('repRegEmail', inputsState.email || '');

          navigate(routerPath.registeredRepresentative);
        });
    } else {
      loadCaptchaEnginge(5, 'white', 'black', 'lower');
    }
  };

  return (
    <RegisterRepresentativeContainerUI>
      <BlueLayout>Регистрация представителя издательств</BlueLayout>
      <FormBlock white padding="40px">
        <div className="upside-layout">Контактная информация</div>
        <Form onFinish={() => submitForm()} onFinishFailed={() => setOnFinishFailed(true)}>
          <Row style={{ width: '100%' }} gutter={[20, 20]}>
            <Col span={24}>
              <FormItem redstar>
                <p>Фамилия</p>
                <Form.Item
                  label="lastname"
                  name="lastname"
                  rules={[{ required: true, message: 'Фамилия введена неверно', pattern: lastNameRegExp }]}
                >
                  <FormInput
                    placeholder="Иванов"
                    value={inputsState.last_name}
                    onChange={(e) => setInputs({ ...inputsState, last_name: e.target.value })}
                  />
                </Form.Item>
              </FormItem>
            </Col>
            <Col span={10}>
              <FormItem redstar>
                <p>Имя</p>
                <Form.Item
                  label="firstname"
                  name="firstname"
                  rules={[{ required: true, message: 'Имя введено неверно', pattern: userNameRegExp }]}
                >
                  <FormInput
                    placeholder="Иван"
                    value={inputsState.first_name}
                    onChange={(e) => setInputs({ ...inputsState, first_name: e.target.value })}
                  />
                </Form.Item>
              </FormItem>
            </Col>
            <Col span={10}>
              <FormItem redstar>
                <p>Отчество</p>
                <Form.Item
                  label="patronymic"
                  name="patronymic"
                  rules={[{ required: true, message: 'Отчество введено неверно', pattern: patronymicRegExp }]}
                >
                  <FormInput
                    placeholder="Иванович"
                    value={inputsState.patronymic}
                    onChange={(e) => setInputs({ ...inputsState, patronymic: e.target.value })}
                  />
                </Form.Item>
              </FormItem>
            </Col>
            <Col span={10}>
              <FormItem
                redstar
                rules={[
                  () => ({
                    validator() {
                      if (inputsState?.phone && inputsState.phone.length === 22) {
                        return Promise.resolve();
                      }

                      return Promise.reject(new Error('Номер телефона введен неверно'));
                    },
                  }),
                ]}
                label="phone"
                name="phone"
              >
                <p>Телефон</p>
                <CustomMaskInput
                  mask="+7 (999) 999 - 99 - 99"
                  error={onFinishFailed}
                  maskChar=""
                  value={inputsState?.phone}
                  onChange={(e) => setInputs({ ...inputsState, phone: e.target.value })}
                />
              </FormItem>
            </Col>
            <Col span={10}>
              <FormItem redstar>
                <p>Email</p>
                <Form.Item
                  label="email"
                  name="email"
                  rules={[{ required: true, message: 'Адрес почты введен неверно', pattern: emailRegExp }]}
                >
                  <FormInput
                    onChange={(e) => setInputs({ ...inputsState, email: e.target.value })}
                    value={inputsState.email}
                    placeholder="ivanov@mail.ru"
                  />
                </Form.Item>
              </FormItem>
            </Col>
            <Col span={24}>
              <FormItem redstar>
                <p>Издательство</p>
                <Form.Item
                  label="publisher"
                  name="publisher"
                  rules={[
                    {
                      validator: () =>
                        inputsState.publisher
                          ? Promise.resolve()
                          : Promise.reject(new Error('Пожалуйста, выберите издательство')),
                    },
                  ]}
                >
                  {' '}
                  <CustomSelect
                    onChange={(value, option) => {
                      setInputs({
                        ...inputsState,
                        publisher: value ? { id: parseInt(Object.values(option)[0]) } : undefined,
                      });
                    }}
                    value={
                      inputsState.publisher
                        ? publishersList.find((el) => el.id === inputsState?.publisher?.id)?.title
                        : ''
                    }
                    defaultValue="Все"
                    showSearch
                  >
                    <Option key={undefined} value={undefined}>
                      {''}
                    </Option>
                    {publishersList &&
                      publishersList.length > 0 &&
                      publishersList.map((el) => (
                        <Option key={el.id} value={el.title}>
                          {el.title}
                        </Option>
                      ))}
                  </CustomSelect>
                </Form.Item>
              </FormItem>
            </Col>
            <Col span={24}>
              <FormItem redstar>
                <p>Пароль</p>
                <Form.Item
                  label="password"
                  name="password"
                  rules={[
                    { required: true, message: 'Пожалуйста, введите пароль' },
                    { min: 12, message: 'Пароль должен содержать больше 12 символов' },
                    { max: 32, message: 'Пароль должен содержать меньше 32 символов' },
                  ]}
                >
                  <FormInput
                    type="password"
                    onChange={(e) => setInputs({ ...inputsState, password: e.target.value })}
                    value={inputsState.password}
                  />
                </Form.Item>
              </FormItem>
            </Col>
            <Col span={24}>
              <FormItem redstar>
                <p>Повторите пароль</p>
                <Form.Item
                  label="passwordRepeat"
                  name="passwordRepeat"
                  rules={[
                    {
                      validator: (_, value) =>
                        value === inputsState.password && value
                          ? Promise.resolve()
                          : Promise.reject(
                              value ? new Error('Пароли не совпадают') : new Error('Пожалуйста, повторите пароль'),
                            ),
                    },
                  ]}
                >
                  <FormInput
                    type="password"
                    onChange={(e) => setInputs({ ...inputsState, passwordRepeat: e.target.value })}
                    value={inputsState.passwordRepeat}
                  />
                </Form.Item>
              </FormItem>
            </Col>
            <DownloadCol noBorder span={24}>
              <FormItem gap="10px" redstar>
                <p>Подтверждение полномочий</p>
                <br />
                <Space align="center">
                  <UploadComponent
                    fileSize={5000000}
                    onClear={onFileRemoveHandler}
                    value={loadedFile && { title: loadedFile.title, id: loadedFile.id }}
                    formats={['.pdf']}
                    onChange={beforeUploadFile}
                  />
                </Space>
                <p>рекомендуется прикреплять файл в формате .pdf (максимальный размер файла 5Мб )</p>
              </FormItem>
            </DownloadCol>
            <Col span={24} />
            <Col span={24}>
              <LoadCanvasTemplate reloadText="Обновить код картинки" />
              <br />
              <FormItem redstar>
                <div className="text">Введите код с картинки</div>
                <FormInput value={captchaInputState} onChange={(e) => setCaptchaInput(e.target.value)} />
                {captchaState === false && <ErrorMessage>Неверно введен код с картинки</ErrorMessage>}
              </FormItem>
            </Col>
            <Col span={24}>
              <Checkbox onChange={(e) => setInputs({ ...inputsState, agree: e.target.checked })}>
                <FormItem>
                  <Form.Item
                    name="agree-item"
                    label="agree-item"
                    rules={[
                      {
                        validator: () =>
                          inputsState.agree ? Promise.resolve() : Promise.reject('Вы не подтвердили свое согласие'),
                      },
                    ]}
                  >
                    Я подтверждаю свое{' '}
                    <a href="https://fpu.edu.ru" target="_blank" rel="noreferrer">
                      согласие
                    </a>{' '}
                    на обработку моих персональных данных
                  </Form.Item>
                </FormItem>
              </Checkbox>
            </Col>
            <Col span={24}>
              <div className="to-center">
                <CustomButton type="submit">Отправить</CustomButton>
              </div>
            </Col>
          </Row>
        </Form>
      </FormBlock>
    </RegisterRepresentativeContainerUI>
  );
};
